import useLocalStorage from '../hooks/useLocalStorage';
import { TextInput } from '../components/shared/input/Input';
import { PrimaryButton } from '../components/ui/buttons';
import { useEffect, useState, type SubmitEvent } from 'react';
import { toast } from 'react-toastify';
import { Heading1 } from '../components/headings';
import APIClient from '../api/APIClient';
import { AxiosError } from 'axios';
import renderToastError from '../utils/renderToastError';
import { useMutation } from '@tanstack/react-query';
import FloatingLoadingSpinner from '../components/ui/FloatingLoadingSpinner';
import useNavbarNotification from '../context/navbarNotification/useNavbarNotification';

const SHOULD_PROTECT = import.meta.env.MODE === 'staging';

export default function BetaGuard({ children }: { children?: React.ReactNode }) {
    const [accessToken, setAccessToken] = useLocalStorage<string>('accessToken');
    const [hasAccess, setHasAccess] = useState(false);

    const mutation = useMutation({
        mutationFn: (key: string) => APIClient.post('/beta/enter', { key }),
        onError: (err: AxiosError) => void toast.error(renderToastError.render({ data: err })),
        onSuccess: () => {
            setHasAccess(true);
            toast.success('Access granted');
        },
    });

    function handleSubmit(e: SubmitEvent) {
        e.preventDefault();
        if (!accessToken || accessToken === '') return toast.error('Please enter a key');

        mutation.mutate(accessToken);
    }

    const notifs = useNavbarNotification();

    useEffect(() => {
        if (SHOULD_PROTECT) {
            notifs.warning('You are currently on the beta version of GDDL.');

            if (accessToken) {
                mutation.mutate(accessToken);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (SHOULD_PROTECT && !hasAccess) {
        return (
            <div className='bg-theme-700 text-theme-text min-h-screen flex items-center justify-center'>
                <div>
                    <Heading1>GDDL Beta</Heading1>
                    <form onSubmit={handleSubmit} className='my-4'>
                        <TextInput
                            value={accessToken}
                            onChange={(e) => setAccessToken(e.target.value)}
                            placeholder='Access key'
                        />
                        <PrimaryButton type='submit' disabled={mutation.isPending}>
                            Enter
                        </PrimaryButton>
                    </form>
                    {mutation.isPending && <FloatingLoadingSpinner />}
                </div>
            </div>
        );
    }

    return children;
}
